<template>
  <div class="choice-step">
    <div class="choice-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Brand Images Row -->
      <div v-if="brandsData && brandsData.length > 0" class="brands-section">
        <div class="brands-grid">
          <div class="brand-item" v-for="brand in brandsData" :key="brand.id">
            <img :src="brand.logo" :alt="brand.name" class="brand-logo" />
            <img :src="brand.brand_name_logo" :alt="brand.name" class="brand_name_logo" />
          </div>
        </div>
      </div>

      <!-- Choice Buttons -->

    </div>
    <div class="choice-buttons-container">
        <span class="saved-answers"> Ответы сохранены 
          <i class="pi pi-bookmark" :color="'var(--active-100)'"></i>
        </span>
          <div class="choice-buttons">
            <custom-button
              v-for="option in step.options"
              :key="option.value"
              rounded
              @click="handleChoice(option)"
              :class="['choice-btn', option.action === 'go_to_results' ? 'results-btn' : 'continue-btn']"
              :severity="option.action === 'go_to_results' ? 'danger' : undefined"
            >
              {{ option.label }}
            </custom-button>
          </div>
      </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useSurveyModal } from '@/features/survey/store'
import CustomButton from '@/components/UI/CustomButtom/CustomButton.vue'
import type { SurveyStep, ChoiceOption } from '@/features/survey/types/survey-config'

interface Props {
  step: SurveyStep & { type: 'choice' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const router = useRouter()

const brandsData = [
  {
    id: 1,
    name: 'Balenciaga',
    logo: '/img/survey/brands_logo/image.png',
    brand_name_logo: '/img/survey/brands_name/Balenciaga.png'
  },
  {
    id: 2,
    name: 'Dior',
    logo: '/img/survey/brands_logo/image-1.png',
    brand_name_logo: '/img/survey/brands_name/Dior.png'
  },
  {
    id: 3,
    name: 'LV',
    logo: '/img/survey/brands_logo/image-2.png',
    brand_name_logo: '/img/survey/brands_name/LV.png'
  },
  {
    id: 4,
    name: 'Prada',
    logo: '/img/survey/brands_logo/image-3.png',
    brand_name_logo: '/img/survey/brands_name/Prada.png'
  }
]

async function handleChoice(option: ChoiceOption) {
  // Обновляем данные
  surveyStore.updateSurveyData(props.step.id, option.value)

  if (option.action === 'go_to_results') {
    // Отправляем данные на бекенд и редиректим на страницу результатов
    try {
      await surveyStore.submitSurvey()
      surveyStore.hideSurveyModal()
      router.push('/profile/sizes')
    } catch (error) {
      console.error('Error submitting survey:', error)
      // В случае ошибки все равно закрываем модалку и редиректим
      surveyStore.hideSurveyModal()
      router.push('/profile/sizes')
    }
  } else if (option.action === 'go_to_step' && option.targetStepId) {
    surveyStore.goToStepById(option.targetStepId)
  } else {
    // Ждем обновления реактивности перед переходом к следующему шагу
    // Это важно для условных шагов, которые становятся видимыми после обновления данных
    await nextTick()
    surveyStore.nextStep()
  }
}
</script>

<style scoped>
.choice-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.choice-content {
  max-width: 500px;
  width: 100%;
}

.title-section {
  margin-bottom: 24px;
  gap:8px !important;
  text-align: left;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--primary-blue-100);
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-size: 16px;
  color: var(--primary-blue-80);
  line-height: 1.5;
  margin: 0;
}

.brands-section {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 40px;
  align-self: stretch;
}

.brands-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--primary-blue-100);
  margin: 0 0 16px 0;
}

.brands-grid {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 8px;
}

.brands-grid::-webkit-scrollbar {
  display: none;
}

.brands-grid {
  scrollbar-width: none;
}

.brand-item {
  display: flex;
  width: 60px;
  height: 80px;
  align-items: center;
  gap: 10px;
  position: relative;
  border-radius: var(--Corner-radius-S, 12px);
  background: var(--Color-White-100, #FFF);
}

.brand-logo {
  flex: 1 0 0;
  align-self: stretch;
  border-radius: var(--Corner-radius-S, 12px);
}

.brand_name_logo {
  width: 60px;
  height: 36px;
  position: absolute;
  top: 22px;
}

.choice-buttons-container {
  display: flex;
  align-self: stretch;
  justify-content: space-between;
  align-items: center;
  padding: 40px 40px 40px 40px;
}

.saved-answers {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-XS, 4px);
  align-self: stretch;
  color:var(--active-100);
}

.choice-buttons {
  display: flex;
  gap: 16px;
}

.choice-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 56px;
}

@media (min-width: 393px) and (max-width: 767px) {
  .choice-step {
    padding: 0;
    flex:1 0 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    flex:1 0 0;
    padding: 40px 20px 20px;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: var(--primary-blue-100);
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
    color: var(--primary-blue-80);
  }

  .choice-buttons {
    align-self: stretch;
    margin-bottom: 0;
  }

  .brands-grid {
    gap: 8px;
  }

  .choice-buttons-container {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: 20px ;
  }

  .choice-buttons {
    flex-direction: column;
    justify-content: center;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .choice-step {
    padding: 0;
    flex:1 0 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 40px 20px 20px;
    flex:1 0 0;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: var(--primary-blue-100);
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
    color: var(--primary-blue-80);
  }

  .choice-buttons {
    align-self: stretch;
    margin-bottom: 0;
  }

  .choice-buttons-container {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }

  .choice-buttons {
    flex-direction: column;
    justify-content: center;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .choice-step {
    padding: 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 40px;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .choice-buttons-container {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }

  .choice-buttons {
    flex-direction: column;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: var(--primary-blue-100);
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: var(--primary-blue-80);
  }

  .choice-buttons {
    align-self: stretch;
    margin-bottom: 0;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .choice-step {
    padding: 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 40px;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: var(--primary-blue-100);
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: var(--primary-blue-80);
  }

  .choice-buttons {
    align-self: stretch;
    margin-bottom: 0;
  }
}

@media (min-width: 1920px) {
  .choice-step {
    padding: 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 40px 120px;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: var(--primary-blue-100);
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: var(--primary-blue-80);
  }

  .choice-buttons {
    align-self: stretch;
    margin-bottom: 0;
  }
}

@media (max-width: 480px) {
  .choice-buttons {
    gap: 12px;
  }

  .choice-btn {
    padding: 14px 20px;
    font-size: 15px;
  }

  .brands-grid {
    gap: 8px;
  }
}
</style>

